import { useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SrpFilterName, SrpFilterValues } from 'types/srpFilters';
import State from 'types/State';
import { DeepPartial } from 'types/util';
import {
  getSrpFiltersInitialized,
  getSrpFilterValue,
  getSrpFilterValues,
} from 'redux-implementations/selectors';
import {
  setSrpFilters,
  clearSrpFilters,
  srpFilterInitialValues,
  srpFiltersInitialState,
} from 'redux-implementations/srpFilters';
import { AdGlobalRefreshContext } from 'contexts/AdGlobalRefreshContext';

export { srpFilterInitialValues, srpFiltersInitialState };

function useFilterValue<N extends SrpFilterName>(name: SrpFilterName): SrpFilterValues[N] {
  return useSelector((state: State) => getSrpFilterValue<SrpFilterValues[N]>(state, name));
}

type SrpFilterSetter<N extends SrpFilterName> = (value: SrpFilterValues[N]) => void;
function useSetFilterValue<N extends SrpFilterName>(name: SrpFilterName): SrpFilterSetter<N> {
  const { refreshGlobalAds } = useContext(AdGlobalRefreshContext);
  const dispatch = useDispatch();
  return (value: SrpFilterValues[N]) => {
    refreshGlobalAds();
    dispatch(setSrpFilters({ [name]: value }));
  };
}

export interface UseSrpFilterResult<N extends SrpFilterName> {
  setValue: SrpFilterSetter<N>;
  value: SrpFilterValues[N];
}
function useFilter<N extends SrpFilterName>(filterName: SrpFilterName): UseSrpFilterResult<N> {
  const value = useFilterValue<N>(filterName);
  const setValue = useSetFilterValue<N>(filterName);
  return { value, setValue };
}

//
// exported hooks
//
export function useSrpFiltersInitialized(): boolean {
  return useSelector(getSrpFiltersInitialized);
}

export type SetSrpFilterValues = (state: DeepPartial<SrpFilterValues>) => void;

export interface UseSrpFiltersResult {
  values: SrpFilterValues;
  setValues: SetSrpFilterValues;
}

export function useSrpFilters(): UseSrpFiltersResult {
  const dispatch = useDispatch();

  const values = useSelector<State, SrpFilterValues>(getSrpFilterValues);

  const setValues = useCallback(
    (newValues: DeepPartial<SrpFilterValues>): void => {
      dispatch(setSrpFilters(newValues));
    },
    [dispatch]
  );

  return { values, setValues };
}

export function useClearSrpFilters(): () => void {
  const dispatch = useDispatch();
  return () => {
    dispatch(clearSrpFilters());
  };
}

export function usePageFilter(): UseSrpFilterResult<SrpFilterName.Page> {
  return useFilter(SrpFilterName.Page);
}

export function useLimitFilter(): UseSrpFilterResult<SrpFilterName.Limit> {
  return useFilter(SrpFilterName.Limit);
}

export function useYearFilter(): UseSrpFilterResult<SrpFilterName.Year> {
  return useFilter(SrpFilterName.Year);
}

export function useConditionFilter(): UseSrpFilterResult<SrpFilterName.Condition> {
  return useFilter(SrpFilterName.Condition);
}

export function useMakeModelFilter(): UseSrpFilterResult<SrpFilterName.MakeModel> {
  return useFilter(SrpFilterName.MakeModel);
}

export function useTrimsFilter(): UseSrpFilterResult<SrpFilterName.Trims> {
  return useFilter(SrpFilterName.Trims);
}

export function useCylindersFilter(): UseSrpFilterResult<SrpFilterName.Cylinders> {
  return useFilter(SrpFilterName.Cylinders);
}

export function useRadiusFilter(): UseSrpFilterResult<SrpFilterName.Radius> {
  return useFilter(SrpFilterName.Radius);
}

export function useSortByFilter(): UseSrpFilterResult<SrpFilterName.SortBy> {
  return useFilter(SrpFilterName.SortBy);
}

export function usePriceFilter(): UseSrpFilterResult<SrpFilterName.Price> {
  return useFilter(SrpFilterName.Price);
}

export function useDoorsFilter(): UseSrpFilterResult<SrpFilterName.Doors> {
  return useFilter(SrpFilterName.Doors);
}

export function useAdvancedFilter(): UseSrpFilterResult<SrpFilterName.Advanced> {
  return useFilter(SrpFilterName.Advanced);
}

export function useBodyTypesFilter(): UseSrpFilterResult<SrpFilterName.BodyTypes> {
  return useFilter(SrpFilterName.BodyTypes);
}

export function useColorsFilter(): UseSrpFilterResult<SrpFilterName.Colors> {
  return useFilter(SrpFilterName.Colors);
}

export function useDrivetrainFilter(): UseSrpFilterResult<SrpFilterName.Drivetrain> {
  return useFilter(SrpFilterName.Drivetrain);
}

export function usePowertrainFilter(): UseSrpFilterResult<SrpFilterName.Powertrain> {
  return useFilter(SrpFilterName.Powertrain);
}

export function useFuelEfficiencyFilter(): UseSrpFilterResult<SrpFilterName.FuelEfficiency> {
  return useFilter(SrpFilterName.FuelEfficiency);
}

// eslint-disable-next-line max-len
export function useFeaturesTechnologyFilter(): UseSrpFilterResult<SrpFilterName.FeaturesTechnology> {
  return useFilter(SrpFilterName.FeaturesTechnology);
}

export function useLocationFilter(): UseSrpFilterResult<SrpFilterName.Location> {
  return useFilter(SrpFilterName.Location);
}

export function useMileageFilter(): UseSrpFilterResult<SrpFilterName.Mileage> {
  return useFilter(SrpFilterName.Mileage);
}

export function useTransmissionFilter(): UseSrpFilterResult<SrpFilterName.Transmission> {
  return useFilter(SrpFilterName.Transmission);
}

export function useTruckOptionsFilter(): UseSrpFilterResult<SrpFilterName.TruckOptions> {
  return useFilter(SrpFilterName.TruckOptions);
}

export function useCategoryFilter(): UseSrpFilterResult<SrpFilterName.Category> {
  return useFilter(SrpFilterName.Category);
}
